import {
  DateDisplay,
  DatePickerCustomInput
} from '@library/components/datePicker';
import { type DateObjectUnits, DateTime } from 'luxon';
import { classNames, isStatusRemoved } from '@library/utils';
import { formatWithTimezoneChange, isTimeEqual } from '@library/utils/dateTime';
import { CalendarContainer } from '@library/components/datePicker/CalendarContainer';
import { DatePickerButton } from '@library/components/buttons';
import { PrizepicksDatePicker } from '@library/components/datePicker/PrizepicksDatePicker';
import type { Status } from '@library/types/projection';
import { TimePickerContext } from '@library/components/datePicker/TimePickerContext';
import { useState } from 'react';
import { useTimePickerContext } from '@library/hooks/useTimePickerContext';
import whiteArrowDownIcon from '@library/assets/images/icons/white-arrow-down.svg';

export interface PlayerModalProjectionStartTimePickerProps {
  dateError?: boolean;
  disabled?: boolean;
  handleChange: (newDate: DateTime) => void;
  initialStartTime: string;
  startTime: string;
  status?: Status;
}

export const PlayerModalProjectionStartTimePicker = ({
  dateError = false,
  disabled = false,
  handleChange,
  initialStartTime,
  startTime,
  status = 'pre_game'
}: PlayerModalProjectionStartTimePickerProps) => {
  const [datePickerOpen, setDatePickerOpen] = useState(false);

  const startDate = DateTime.fromISO(startTime);
  const initialStartDate = DateTime.fromISO(initialStartTime);
  const timePickerContext = useTimePickerContext({
    date: startDate,
    initialDate: startDate,
    onChange: (dateObectUnits: DateObjectUnits) => {
      handleChange(startDate.set(dateObectUnits));
    }
  });

  const hasChanged = !isTimeEqual({
    dateValue: initialStartDate,
    dateToCompareAgainst: startDate
  });

  const handleCloseCalendar = () => {
    if (timePickerContext.validations.isError) {
      handleChange(initialStartDate);

      timePickerContext.resetTime();
    }
  };

  const handleOpenCalendar = () => {
    if (timePickerContext.initialTime !== initialStartDate) {
      timePickerContext.setInitialTime(initialStartDate);
    }
  };

  const handleChooseDate = (date: Date) => {
    const newDate = DateTime.fromJSDate(date);

    const newDateObject = {
      day: newDate.day,
      month: newDate.month,
      year: newDate.year
    };

    const newProposedDate = startDate.set(newDateObject);

    handleChange(newProposedDate);

    timePickerContext.validateTime({
      inputName: 'hour',
      newTime: newProposedDate,
      value: formatWithTimezoneChange(newProposedDate, 'h')
    });
  };

  return (
    <td className="text-soFresh-100 items-center justify-center px-0 py-4 text-center">
      <div className="mx-auto w-fit">
        <TimePickerContext.Provider value={timePickerContext}>
          <PrizepicksDatePicker
            calendarContainer={CalendarContainer}
            customInput={
              <DatePickerCustomInput
                className={classNames(
                  datePickerOpen
                    ? 'bg-soFresh-120 !border-msJackson'
                    : 'bg-soFresh-110',
                  isStatusRemoved(status) ? 'opacity-50' : '',
                  disabled ? 'opacity-50' : 'hover:bg-soFresh-120',
                  dateError ? '!border-roses' : '',
                  !dateError && hasChanged ? 'border-msJackson' : '',
                  !dateError && !hasChanged ? 'border-soFresh-140' : '',
                  'text-soClean-140 grid h-8 w-full rounded-md border border-solid text-xs'
                )}
              >
                <DatePickerButton
                  className="h-8 disabled:cursor-not-allowed"
                  disabled={disabled}
                >
                  <DateDisplay className="flex items-center gap-1 whitespace-nowrap px-2">
                    <span className="font-heebo leading-5">
                      {formatWithTimezoneChange(startDate, 't ZZZZ')}
                    </span>
                    <span className="relative inset-y-0 right-0 flex size-3">
                      <img
                        alt="Chevron Down icon"
                        className={classNames(
                          datePickerOpen ? 'rotate-180' : ''
                        )}
                        src={whiteArrowDownIcon}
                      />
                    </span>
                  </DateDisplay>
                </DatePickerButton>
              </DatePickerCustomInput>
            }
            closeOnSelect={false}
            dateFormat="MMMM d, yyyy h:mm aa"
            disabled={disabled}
            onCalendarClose={handleCloseCalendar}
            onCalendarOpen={handleOpenCalendar}
            handleChange={handleChooseDate}
            monthsShown={1}
            open={datePickerOpen}
            setOpen={setDatePickerOpen}
            value={new Date(formatWithTimezoneChange(startDate, 'D'))}
          />
        </TimePickerContext.Provider>
      </div>
    </td>
  );
};
